import { captureError } from '@/lib/monitoring'
import type { ISBNBookData } from '@/services/isbn'
import { supabase } from './client'

export interface ISBNDataRow {
  isbn: string
  book_data: ISBNBookData
  created_by: string | null
  created_at: string
  updated_at: string
}

/**
 * Get book data from local cache by ISBN
 */
export async function getISBNDataFromCache(isbn: string): Promise<ISBNBookData | null> {
  try {
    const cleanISBN = isbn.replace(/[- ]/g, '')

    const { data, error } = await supabase
      .from('isbn_data')
      .select('book_data')
      .eq('isbn', cleanISBN)
      .maybeSingle()

    if (error) {
      throw error
    }

    return data?.book_data || null
  } catch (error) {
    captureError(error as Error, { context: 'Error fetching ISBN data from cache' })
    return null
  }
}

/**
 * Save book data to local cache
 */
export async function saveISBNDataToCache(isbn: string, bookData: ISBNBookData): Promise<void> {
  try {
    const cleanISBN = isbn.replace(/[- ]/g, '')

    const { data: user } = await supabase.auth.getUser()

    const { error } = await supabase.from('isbn_data').upsert(
      {
        isbn: cleanISBN,
        book_data: bookData,
        created_by: user.user?.id || null,
      },
      {
        onConflict: 'isbn',
      }
    )

    if (error) {
      throw error
    }
  } catch (error) {
    captureError(error as Error, { context: 'Error saving ISBN data to cache' })
    // Don't throw - cache failure shouldn't break the flow
  }
}

/**
 * Get all cached ISBN data (for admin/debug purposes)
 */
export async function getAllISBNData(): Promise<ISBNDataRow[]> {
  try {
    const { data, error } = await supabase
      .from('isbn_data')
      .select('*')
      .order('created_at', { ascending: false })

    if (error) {
      throw error
    }

    return data || []
  } catch (error) {
    captureError(error as Error, { context: 'Error fetching all ISBN data' })
    return []
  }
}

/**
 * Delete ISBN data from cache
 */
export async function deleteISBNData(isbn: string): Promise<void> {
  try {
    const cleanISBN = isbn.replace(/[- ]/g, '')

    const { error } = await supabase.from('isbn_data').delete().eq('isbn', cleanISBN)

    if (error) {
      throw error
    }
  } catch (error) {
    captureError(error as Error, { context: 'Error deleting ISBN data from cache' })
    throw error
  }
}
